import { GupMessage } from '@/components/gup-message';
import GupStart from './-components/GupStart';
import { clearGupVisit, getGupVisit } from '@/lib/gup';
import { isGupCompanyProfileError } from '@/services/gup-errors';
import { prepareGupEntry } from '@/services/gup-entry';
import { switchUserProfile } from '@/api/backend/profile';
import { getGisFilterZones } from '@/api/backend/reference';
import { getNextInvolvedTask, saveTaskForm, startProcess } from '@/api/backend/workflow';
import { CompanyProfileType, NextInvolvedTaskResponseType } from '@/api/backend/types';
import { useAnalytics } from '@/hooks/useAnalytics';
import { queryClient } from '@/services/queryClient';
import { useAuthStore } from '@/store/auth';
import { Spinner } from '@opaz/ui-components';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate, useSearch } from '@tanstack/react-router';
import { AxiosError } from 'axios';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FlowableTaskLayout from '../processes/$processInstanceId/task/-components/FlowableTaskLayout';
import StartServiceWizard from './-components/StartServiceWizard';

const INVESTMENT_APPLICATION_SERVICE_DEFINITION_CODE = '100' as const;

export const Route = createFileRoute('/_auth/start-service/')({
  validateSearch: (
    search,
  ): {
    srv: string;
    encRequest?: string;
    zone: string | undefined;
    plotId: string | undefined;
    lang: string | undefined;
    ref: string | undefined;
    cr: string | undefined;
  } => {
    if (!search.srv && search.encRequest === undefined) {
      throw new Error('Service name (srv) is required in the search parameters.');
    }

    return {
      srv: (search.srv as string | undefined) ?? '',
      encRequest: search.encRequest as string | undefined,
      zone: search.zone as string | undefined,
      plotId: search.plotId as string | undefined,
      lang: search.lang as string | undefined,
      ref: search.ref as string | undefined,
      cr: search.cr as string | undefined,
    };
  },
  beforeLoad: async ({ location, search }) => {
    if (search.encRequest === undefined) {
      clearGupVisit();
      return { gupEntry: undefined };
    }
    const gupEntry = await prepareGupEntry(location.pathname + location.searchStr);
    if (gupEntry.login) throw redirect({ to: '/login', search: { redirect: location.pathname + location.searchStr } });
    if (gupEntry.registration)
      throw redirect({
        to: '/signup',
        search: { redirect: location.pathname + location.searchStr, txId: undefined, mpkId: undefined },
      });
    return { gupEntry };
  },
  component: LandingComponent,
  head: () => {
    return {
      meta: [
        {
          title: 'Start Service - OPAZ E-Service Portal',
        },
      ],
    };
  },
});

function LandingComponent() {
  const { gupEntry } = Route.useRouteContext();
  if (gupEntry?.block || gupEntry?.failure) return <GupMessage entry={gupEntry} />;
  if (gupEntry?.visit) return <GupStart visit={gupEntry.visit} />;
  return <RouteComponent />;
}

function RouteComponent() {
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const { activeProfile, userProfile } = useAuthStore();
  const analytics = useAnalytics();

  const {
    srv: serviceDefinitionCode,
    zone: zoneCode,
    plotId,
    lang,
    cr: crNumber,
    ref: engagementChannelRef,
  } = useSearch({ from: '/_auth/start-service/' });

  const autoSelectedProfile = crNumber
    ? userProfile.profiles
        .filter((profile) => profile.type === 'COMPANY')
        .find((profile) => {
          const companyProfile = profile as CompanyProfileType;
          return companyProfile.companyDetails.crNumber === crNumber.toString();
        })
    : undefined;

  const [confirmedProfileId, setConfirmedProfileId] = useState<string | undefined>(() => {
    if (userProfile.profiles.length === 1) {
      return userProfile.profiles[0].id;
    } else if (autoSelectedProfile) {
      return autoSelectedProfile.id;
    }
    return undefined;
  });

  if (lang && lang !== i18n.language) {
    i18n.changeLanguage(lang);
  }

  const executeCustomLogicByServiceCode = async (nextTask: NextInvolvedTaskResponseType) => {
    if (serviceDefinitionCode == INVESTMENT_APPLICATION_SERVICE_DEFINITION_CODE && zoneCode) {
      const zones = await getGisFilterZones();
      const zone = zones.find((z) => z.zoneGisCode === zoneCode);

      await saveTaskForm(nextTask.id, {
        engagementChannelRef: engagementChannelRef ?? null,
        investmentApp: {
          gisMap: {
            filter: {
              zone,
            },
            proposedPlots: plotId ? [Number(plotId)] : [],
          },
        },
      });
    } else {
      await saveTaskForm(nextTask.id, {
        engagementChannelRef: engagementChannelRef ?? null,
      });
    }
  };

  const { data: isProfileSwitched } = useQuery({
    queryKey: ['switchProfile', confirmedProfileId, activeProfile?.id],
    queryFn: async () => {
      if (confirmedProfileId === activeProfile?.id) {
        return true;
      }

      await switchUserProfile(confirmedProfileId!);

      await queryClient.invalidateQueries({ queryKey: ['userProfile'] });

      return true;
    },
    enabled: !!confirmedProfileId,
  });

  const trackServiceApplicationStart = useCallback(
    (serviceName: string) => {
      if (analytics) {
        const trimmedServiceName = serviceName.trim();
        analytics.trackServiceApplicationStart(serviceDefinitionCode, trimmedServiceName);
      }
    },
    [analytics, serviceDefinitionCode],
  );

  const startProcessQuery = useQuery<boolean, AxiosError>({
    queryKey: ['startProcess', serviceDefinitionCode, zoneCode, analytics],
    queryFn: async () => {
      const createdProcess = await startProcess({
        serviceDefinitionCode,
        serviceCode: serviceDefinitionCode,
        zoneCode,
      });

      const nextTask = await getNextInvolvedTask(createdProcess.id);
      await executeCustomLogicByServiceCode(nextTask);

      trackServiceApplicationStart(createdProcess.processDefinitionName);

      navigate({
        to: '/processes/$processInstanceId/task/$taskId',
        params: {
          processInstanceId: createdProcess.id,
          taskId: nextTask.id,
        },
        search: {
          responseToken: undefined,
        },
      });

      return true;
    },
    enabled: isProfileSwitched === true,
  });

  const gupVisit = getGupVisit();
  if (startProcessQuery.isError && gupVisit) {
    return (
      <GupMessage
        entry={{ visit: gupVisit, failure: 'failed' }}
        message={isGupCompanyProfileError(startProcessQuery.error) ? t('gup.blocks.individual') : undefined}
      />
    );
  }

  if (!confirmedProfileId) {
    return <StartServiceWizard onProfileConfirmed={setConfirmedProfileId} />;
  }

  return (
    <FlowableTaskLayout subHeader={<div />}>
      <div className="flex justify-center items-center h-[400px]">
        <Spinner />
      </div>
    </FlowableTaskLayout>
  );
}
